import { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Spec utilisateur Feb 2026 — composant de changement de mot de passe
 * réutilisable pour les 3 espaces (visiteur, admin, établissement).
 *
 * Règles de robustesse (alignées avec `_validate_new_password` backend) :
 *  • Minimum 8 caractères
 *  • Au moins 1 lettre, 1 chiffre, 1 caractère spécial
 *  • Le nouveau mot de passe DOIT être différent de l'actuel
 *
 * Après succès, le backend envoie un email de confirmation au compte.
 */
const POLICY_LABEL =
  "Au moins 8 caractères, dont 1 lettre, 1 chiffre et 1 caractère spécial.";

function checkPolicy(pwd) {
  if (!pwd || pwd.length < 8) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(pwd)) return false;
  if (!/\d/.test(pwd)) return false;
  if (!/[^A-Za-zÀ-ÿ0-9]/.test(pwd)) return false;
  return true;
}

export default function ChangePasswordCard({ endpoint, testIdPrefix = "change-password" }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const policyOk = checkPolicy(next);
  const matchOk = next.length > 0 && next === confirm;
  const distinctOk = next.length > 0 && next !== current;
  const canSubmit = current.length > 0 && policyOk && matchOk && distinctOk && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(endpoint, { currentPassword: current, newPassword: next });
      toast.success(
        "Mot de passe modifié avec succès. Un email de confirmation a été envoyé.",
        { duration: 6000 },
      );
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === "string"
          ? msg
          : "Erreur lors du changement de mot de passe. Veuillez réessayer.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border border-border p-5 md:p-6 bg-white"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 flex items-center justify-center bg-brand text-white">
          <Lock size={18} />
        </div>
        <div>
          <h3 className="font-display text-lg font-black text-ink leading-tight">
            Changer mon mot de passe
          </h3>
          <p className="text-xs text-subtle">Sécurisez l&apos;accès à votre compte</p>
        </div>
      </div>

      <div className="space-y-3">
        {/* Mot de passe actuel */}
        <div>
          <label className="text-xs uppercase tracking-wider text-subtle font-bold block mb-1">
            Mot de passe actuel
          </label>
          <div className="relative">
            <Input
              type={showCurrent ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="pr-10"
              data-testid={`${testIdPrefix}-current`}
            />
            <button
              type="button"
              onClick={() => setShowCurrent((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-ink"
              tabIndex={-1}
              aria-label={showCurrent ? "Masquer" : "Afficher"}
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Nouveau */}
        <div>
          <label className="text-xs uppercase tracking-wider text-subtle font-bold block mb-1">
            Nouveau mot de passe
          </label>
          <div className="relative">
            <Input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="pr-10"
              data-testid={`${testIdPrefix}-new`}
            />
            <button
              type="button"
              onClick={() => setShowNext((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-ink"
              tabIndex={-1}
              aria-label={showNext ? "Masquer" : "Afficher"}
            >
              {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p
            className={`mt-1 text-xs ${
              next.length === 0
                ? "text-subtle"
                : policyOk
                ? "text-emerald-600"
                : "text-red-600"
            }`}
            data-testid={`${testIdPrefix}-policy-hint`}
          >
            {POLICY_LABEL}
          </p>
        </div>

        {/* Confirmation */}
        <div>
          <label className="text-xs uppercase tracking-wider text-subtle font-bold block mb-1">
            Confirmer le nouveau mot de passe
          </label>
          <Input
            type={showNext ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid={`${testIdPrefix}-confirm`}
          />
          {confirm.length > 0 && !matchOk && (
            <p className="mt-1 text-xs text-red-600">
              Les deux mots de passe ne correspondent pas.
            </p>
          )}
          {next.length > 0 && current.length > 0 && !distinctOk && (
            <p className="mt-1 text-xs text-red-600">
              Le nouveau mot de passe doit être différent de l&apos;actuel.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 items-center">
        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 px-6 disabled:opacity-50"
          data-testid={`${testIdPrefix}-submit`}
        >
          {submitting ? "Enregistrement…" : "Mettre à jour"}
        </Button>
        <p className="text-[11px] text-subtle italic">
          Un email de confirmation sera envoyé après modification.
        </p>
      </div>
    </form>
  );
}
